import { useNavigation } from "@/iti-core-react/contexts/navigation";
import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { ItiString } from "@/iti-core-react/utils/iti-string";
import { MenuListDto } from "@/shared/dtos/menu-list-dto";
import { MenuOptionsService } from "@/shared/services/menuOptionsService";
import { RootState } from "@/store";
import {
	setNavigationBlocked,
	showNavigationBlockedMessage,
} from "@/store/navigationBlock/navigationBlockActions";
import ContextMenu from "devextreme-react/cjs/context-menu";
import Tooltip from "devextreme-react/cjs/tooltip";
import TreeView from "devextreme-react/cjs/tree-view";
import {
	ContentReadyEvent,
	ItemClickEvent,
} from "devextreme/ui/tree_view_types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import "./SideNavigationMenu.scss";
import PoweredByIti from "@/shared/components/PoweredByIti/PoweredByIti.tsx";

export interface SideNavigationMenuProps {
	selectedItemChanged: (e: ItemClickEvent) => void;
	openMenu: (e: React.MouseEvent<HTMLDivElement>) => void;
	compactMode: boolean;
	onMenuReady: (e: ContentReadyEvent) => void;
	children: React.ReactNode;
}

export default function SideNavigationMenu({
	compactMode,
	children,
	openMenu,
	selectedItemChanged,
	onMenuReady,
}: Readonly<SideNavigationMenuProps>) {
	const { t } = useLanguage();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [sideMenuItems, setSideMenuItems] = useState<MenuListDto[]>([]);
	const [treeViewComponent, setTreeViewComponent] = useState<any>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [selectedTreeItem, setSelectedTreeItem] = useState<any>(null);
	const [lastClickEvent, setLastClickEvent] = useState<any>(null);
	const contextMenuRef = useRef<any>(null);
	const [tooltipContent, setTooltipContent] = useState<string>("");
	const [tooltipTarget, setTooltipTarget] = useState<any>(null);
	const isNavigationBlocked = useSelector(
		(state: RootState) => state.navigationBlock.isNavigationBlocked,
	);
	const location = useLocation();
	const {
		navigationData: { currentPath },
	} = useNavigation();

	useEffect(() => {
		const loadMenuItems = async () => {
			const res = await MenuOptionsService.getMainMenu();
			if (Array.isArray(res) && res.length > 0) {
				const translatedItems = translateMenuItems(res, t);
				setSideMenuItems(translatedItems);
				localStorage.setItem("sideMenuItems", JSON.stringify(translatedItems));
			}
		};

		loadMenuItems();
	}, [t]);

	useEffect(() => {
		document.body.dispatchEvent(new MouseEvent("click", { bubbles: true }));
	}, [location.pathname]);

	useEffect(() => {
		if (treeViewComponent && currentPath !== undefined) {
			const cleanPath = currentPath.replace(/^\//, "");

			if (selectedTreeItem) {
				treeViewComponent.selectItem(selectedTreeItem.id);
				treeViewComponent.expandItem(selectedTreeItem.id);
				return;
			}

			const parentItem = findParentItemByPath(sideMenuItems, cleanPath);
			if (parentItem) {
				treeViewComponent.selectItem(parentItem.id);
				treeViewComponent.expandItem(parentItem.id);
			} else {
				const currentItem = findItemByPath(sideMenuItems, cleanPath);

				if (currentItem) {
					const parent = sideMenuItems.find((item) =>
						item.contextItems?.some(
							(contextItem) => contextItem.id === currentItem.id,
						),
					);
					if (parent) {
						treeViewComponent.selectItem(parent.id);
						treeViewComponent.expandItem(parent.id);
					} else {
						treeViewComponent.selectItem(currentItem.id);
					}
				} else {
					treeViewComponent.unselectAll();
				}
			}

			if (compactMode) {
				treeViewComponent.collapseAll();
			}
		}
	}, [
		currentPath,
		compactMode,
		treeViewComponent,
		sideMenuItems,
		selectedTreeItem,
	]);

	const getLocaleText = useCallback(
		(item: any) => {
			if (!ItiString.isNullOrWhiteSpace(item.i18n)) {
				return t(item.i18n);
			}
			return item.text;
		},
		[t],
	);

	const handleOpenMenu = useCallback(
		(event: MouseEvent) => {
			if (
				wrapperRef.current &&
				event.target instanceof Node &&
				wrapperRef.current.contains(event.target)
			) {
				openMenu(event as unknown as React.MouseEvent<HTMLDivElement>);
			}
		},
		[openMenu],
	);

	useEffect(() => {
		document.addEventListener("click", handleOpenMenu);
		return () => {
			document.removeEventListener("click", handleOpenMenu);
		};
	}, [handleOpenMenu]);

	const onContentReady = (e: ContentReadyEvent) => {
		setTreeViewComponent(e.component);
		onMenuReady(e);
	};

	const handleContextMenuHiding = useCallback(() => {
		try {
			if (contextMenuRef.current) {
				const instance = contextMenuRef.current.instance();
				if (instance && typeof instance.option === "function") {
					instance.option("items", []);
				}
			}
		} catch (error) {
			console.warn("Error al limpiar items del menú contextual:", error);
		} finally {
			setLastClickEvent(null);
			setSelectedTreeItem(null);
		}
	}, []);

	const handleItemClick = useCallback(
		async (e: ItemClickEvent) => {
			// Solo mostrar mensaje de confirmación si estamos en la ruta second-step
			const currentPath = window.location.pathname;
			const isInSecondStep = currentPath.includes("/second-step/");

			if (isNavigationBlocked && isInSecondStep) {
				try {
					const canNavigate = await dispatch(showNavigationBlockedMessage());
					if (!canNavigate) {
						return;
					}
					// Limpiar el bloqueo inmediatamente después de aceptar
					dispatch(setNavigationBlocked(false));
				} catch (error) {
					return;
				}
			}
			const hasContextItems = e.itemData?.contextItems?.length > 0;

			setLastClickEvent(e);
			setSelectedTreeItem(e.itemData);

			if (hasContextItems && contextMenuRef.current && e.itemData) {
				try {
					const instance = contextMenuRef.current.instance();

					if (!instance) return;

					await new Promise((resolve) => setTimeout(resolve, 0));

					instance.option("items", []);
					instance.option("items", e.itemData.contextItems);

					// Hacia arriba
					const parentNode = e.element.closest(
						`.dx-treeview-node[data-item-id="${e.itemIndex}"]`,
					);

					// Hacia abajo
					const childNode = e.element.querySelector(
						`.dx-treeview-node[data-item-id="${e.itemIndex}"]`,
					);

					const treeNode = parentNode || childNode;

					if (treeNode) {
						const nodeRect = treeNode.getBoundingClientRect();

						instance.option("position", {
							my: "left top",
							at: "left top",
							of: treeNode,
							offset: {
								x: nodeRect.width - 20,
								y: nodeRect.height,
							},
							collision: "flipfit",
						});

						instance.option("target", treeNode);

						setSelectedTreeItem(e.itemData);
						treeViewComponent.selectItem(e.itemData.id);

						requestAnimationFrame(() => {
							if (instance) {
								instance.show();
							}
						});
					}
				} catch (error) {
					console.warn("Error al mostrar menú contextual:", error);
				}
			} else if (e.itemData) {
				try {
					if (contextMenuRef.current) {
						const instance = contextMenuRef.current.instance();
						if (instance && typeof instance.hide === "function") {
							instance.hide();
						}
					}
				} catch (error) {
					console.warn("Error al ocultar menú contextual:", error);
				}

				navigate(e.itemData.link);

				if (treeViewComponent) {
					treeViewComponent.selectItem(e.itemData.id);
				}
				selectedItemChanged(e);
			}
		},
		[
			isNavigationBlocked,
			dispatch,
			selectedItemChanged,
			navigate,
			treeViewComponent,
			compactMode,
		],
	);

	const handleContextMenu = useCallback((e: any) => {
		e.event.preventDefault();
	}, []);

	const contextMenuItemClick = useCallback(
		async (e: any) => {
			// Solo mostrar mensaje de confirmación si estamos en la ruta second-step
			const currentPath = window.location.pathname;
			const isInSecondStep = currentPath.includes("/second-step/");

			if (isNavigationBlocked && isInSecondStep) {
				try {
					const canNavigate = await dispatch(showNavigationBlockedMessage());
					if (!canNavigate) {
						return;
					}
					// Limpiar el bloqueo inmediatamente después de aceptar
					dispatch(setNavigationBlocked(false));
				} catch (error) {
					return;
				}
			}

			if (treeViewComponent && selectedTreeItem) {
				navigate(e.itemData.link);

				setTimeout(() => {
					const parentItem = sideMenuItems.find((item) =>
						item.contextItems?.some(
							(contextItem) => contextItem.id === e.itemData.id,
						),
					);

					if (parentItem) {
						treeViewComponent.selectItem(parentItem.id);
						treeViewComponent.expandItem(parentItem.id);

						selectedItemChanged({
							itemData: e.itemData,
							component: treeViewComponent,
							element: lastClickEvent?.element,
							event: e.event,
							node: lastClickEvent?.node,
						});
					}
				}, 200);
			}

			if (contextMenuRef.current) {
				contextMenuRef.current.instance().hide();
			}
		},
		[
			isNavigationBlocked,
			dispatch,
			navigate,
			selectedTreeItem,
			treeViewComponent,
			selectedItemChanged,
			lastClickEvent,
			sideMenuItems,
		],
	);

	const handlePositioning = useCallback(
		(e: any) => {
			const target =
				lastClickEvent?.event?.target || lastClickEvent?.originalEvent?.target;

			if (!target) {
				e.cancel = true;
				return;
			}

			const isExpanderClick = target.closest(".dx-treeview-expander-icon-stub");
			if (isExpanderClick) {
				e.cancel = true;
			}
		},
		[lastClickEvent],
	);

	const handleItemRendered = useCallback(
		(e: any) => {
			const itemElement = e.element;
			if (!itemElement) return;

			const treeNode = itemElement.querySelector(".dx-treeview-item");
			if (!treeNode) return;

			treeNode.id = `treeview-item-${e.itemData.id}`;

			const item = e.element.querySelector(
				`.dx-treeview-node[data-item-id="${e.itemData.id}"]`,
			);

			item.onmouseenter = (event: MouseEvent) => {
				const node = event.currentTarget as HTMLElement;

				node.setAttribute("id", `tree-node-leaf-${e.itemData.id}`);

				if (!e.itemData?.text) return;

				setTooltipContent(e.itemData.text);
				setTooltipTarget(`#tree-node-leaf-${e.itemData.id}`);
			};

			item.onmouseleave = () => {
				setTooltipContent("");
				setTooltipTarget(null);
			};
		},
		[compactMode, getLocaleText],
	);

	function onContentToolbarReady(e: any) {
		e.component.content().style.backgroundColor = "#3f3f4b";
	}

	return (
		<div
			className={`dx-swatch-additional side-navigation-menu${
				compactMode ? " compact" : ""
			}`}
			ref={wrapperRef}
		>
			{children}
			<div className="menu-container">
				<TreeView
					id="treeViewSmall"
					noDataText=""
					items={sideMenuItems}
					keyExpr="id"
					selectionMode="single"
					focusStateEnabled={false}
					expandEvent="click"
					onItemClick={handleItemClick}
					onContentReady={onContentReady}
					onItemContextMenu={handleContextMenu}
					onItemRendered={handleItemRendered}
					width="100%"
					displayExpr={getLocaleText}
				/>
				<div className="logo-container">
					<PoweredByIti displayWisephereIcon={false} smallLogo={compactMode} />
				</div>

				<Tooltip
					target={tooltipTarget}
					showEvent="mouseenter"
					hideEvent="mouseleave"
					contentRender={() => tooltipContent}
					position="right"
					visible={!!tooltipContent && compactMode}
					hideOnOutsideClick={true}
					onContentReady={onContentToolbarReady}
					animation={{
						show: { type: "fade", duration: 300, from: 0, to: 1 },
						hide: { type: "fade", duration: 300, to: 0 },
					}}
				/>

				<ContextMenu
					ref={contextMenuRef}
					onItemClick={contextMenuItemClick}
					displayExpr="text"
					hideOnOutsideClick={true}
					showEvent="click"
					cssClass="tree-context-menu"
					onShowing={handlePositioning}
					onHiding={handleContextMenuHiding}
					items={selectedTreeItem?.contextItems || []}
				/>
			</div>
		</div>
	);
}

function findItemByPath(items: any[], path: string): any {
	return items.find((item) => item.link === `/${path}`);
}

function findParentItemByPath(items: any[], childPath: string): any {
	return items.find((item) =>
		item.contextItems?.some(
			(contextItem: any) => contextItem.link === childPath,
		),
	);
}

function translateMenuItems(
	items: any[],
	t: (key: string) => string,
): MenuListDto[] {
	return items.map((item) => {
		const translatedItem = { ...item };

		if (translatedItem.i18n) {
			translatedItem.text = t(translatedItem.i18n);
		}

		if (Array.isArray(translatedItem.items)) {
			translatedItem.items = translateMenuItems(translatedItem.items, t);
		}

		if (Array.isArray(translatedItem.contextItems)) {
			translatedItem.contextItems = translateMenuItems(
				translatedItem.contextItems,
				t,
			);
		}

		return translatedItem;
	});
}
